import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../ui/select';
import { DateTimePicker } from '../ui/date-picker';
import { format } from 'date-fns';
import { EventType, EVENT_TYPE_LABELS } from '../../types/client-activity.types';
import { activityService } from '../../lib/activityService';
import { useStore } from '../../store/useStore';

interface AddEventFormProps {
    clientId: string;
    onEventAdded: () => void;
    onCancel?: () => void;
}

export function AddEventForm({ clientId, onEventAdded, onCancel }: AddEventFormProps) {
    const [eventType, setEventType] = useState<EventType>('call');
    const [title, setTitle] = useState('');
    const [notes, setNotes] = useState('');
    // Initialize with current Date
    const [date, setDate] = useState<Date | undefined>(new Date());
    const [loading, setLoading] = useState(false);
    const { addNotification } = useStore();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return;
        if (!date) {
            addNotification({ type: 'error', title: 'Data richiesta' });
            return;
        }

        setLoading(true);
        try {
            await activityService.createEvent({
                client_id: clientId,
                event_type: eventType,
                title: title,
                notes: notes || null,
                event_date: date.toISOString(),
                status: 'open',
                parent_event_id: null,
            });

            addNotification({ type: 'success', title: 'Evento aggiunto' });
            // Reset form
            setTitle('');
            setNotes('');
            setEventType('call');
            setDate(new Date());
            onEventAdded();
        } catch (error) {
            console.error(error);
            addNotification({ type: 'error', title: 'Errore' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border space-y-4">
            <h3 className="font-semibold text-gray-800">Nuova Attività</h3>

            <form onSubmit={handleSubmit} className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="w-full sm:w-1/3">
                        <Select value={eventType} onValueChange={(v) => setEventType(v as EventType)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Tipo" />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-full sm:w-2/3">
                        <DateTimePicker
                            date={date}
                            setDate={setDate}
                        />
                    </div>
                </div>

                <Input
                    placeholder="Titolo evento..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                />

                <Textarea
                    placeholder="Note aggiuntive..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />

                <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">
                    {onCancel && (
                        <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
                            Annulla
                        </Button>
                    )}
                    <Button type="submit" disabled={loading}>
                        {loading ? 'Salvataggio...' : 'Salva Attività'}
                    </Button>
                </div>
            </form>
        </div>
    );
}
